import { CanvasComponent } from "../components/canvas.component";
import { AppComponent } from '../components/app.component';
import {  Injectable } from '@angular/core';
import { EditorContentService } from "./editorcontent.service";
import { CommandsService } from "./commands.service";
import { WatchService } from "./watch.service";
import { LanguageService } from "./language.service";

declare var ace: any;

@Injectable()
export class DebuggerService {
	// Return values determining the behaviour of the execution after a callback
    static EXECUTION_CONTINUE = 1;
	static EXECUTION_SUSPEND = 0;
	
	// Components and services provided via constructor
	private et: EditorContentService;
	private canvasComponent: CanvasComponent;
	private commandsService: CommandsService;
	private watchService: WatchService;

	// Used to mark next and previous commands
	private markerStack = new Array();
	private markerNext;
	private markerPrev;
	private sessionMarkerNext = AppComponent.editor.session;
	private sessionMarkerPrev = AppComponent.editor.session;

	// Used to save contexts and canvases as states for reconstruction
	private visitorStateStack = new Array();
	private canvasStateStack = new Array();

	// Attaches services/components and loads images for watch
	constructor(et: EditorContentService, cc: CanvasComponent, cs: CommandsService, lang: LanguageService) {
		this.et = et;
		this.canvasComponent = cc;
		this.commandsService = cs;
		this.watchService = new WatchService(lang);
	}

	// Take one step forward in execution
    public stepForward(visitor): void {
		// Disable buttons during execution of next step
		this.et.disableButton(this.et.getNextButton());
		this.et.disableButton(this.et.getPrevButton());

		// Clone state and continue executing from the current point
		var nextState = this.deepClone(this.visitorStateStack.slice(-1)[0]);
		visitor.reenterExecution(nextState);
    }

	// Take one step backwards in execution
    public stepBackward(): void {
		// Change markers
		this.sessionMarkerNext.removeMarker(this.markerNext);
		this.sessionMarkerPrev.removeMarker(this.markerPrev);
		this.markerStack.pop();
		const coordsNext = this.markerStack.slice(-1)[0];
		const coordsPrev = this.markerStack.slice(-2)[0];
		this.markerPrev = this.highlightRange(coordsPrev[0], coordsPrev[1], coordsPrev[2], "markerPrevCmd");
		this.markerNext = this.highlightRange(coordsNext[0], coordsNext[1], coordsNext[2], "markerNextCmd");

		// Restore state
		this.visitorStateStack.pop();
        this.canvasStateStack.pop();
        const nextCanvasState = this.canvasStateStack.slice(-1)[0];

		// Restore canvas state
		const nextCanvasStateCopy = this.canvasComponent.deepCloneState(nextCanvasState);
		this.canvasComponent.restoreState(nextCanvasStateCopy);

		// Restore debug watch
		this.watchService.updateDebugWatch(nextCanvasState[0]);

		// Disable step back button if we're on the first command
		if (this.getStateStackLength() == 1) {
			this.et.disableButton(this.et.getPrevButton());
		}
    }

	/*
		Called whenever the visitor hits an internal breakpoint. Debugger decides
		wether execution should just continue (e.g. in nested method calls) or if
		the execution should be suspended. In the latter case, we then wait for
		user input (stepping or aborting the program).
	*/
    public callback(visitorState): number {
        // Check if program is finished
        let ctx = visitorState.ctx;
        if (ctx === null) {
            return DebuggerService.EXECUTION_CONTINUE;
        }

        // Check if command originated from input line and is the only command
        let row = ctx.start.line - 1;
        if (row == 0) {
            let noCommands = ctx.parentCtx.children.length;
            if (noCommands == 1) {
                return DebuggerService.EXECUTION_CONTINUE;
            }
        }

		// Check if callback has exceeded depth 1
        if (this.getCallstackDepth(ctx) > 1) {
            return DebuggerService.EXECUTION_CONTINUE;
		}

        // Save canvas and state of visitor on stacks in order to step back
		let canvasState = this.canvasComponent.getState();
        this.visitorStateStack.push(visitorState);
        this.canvasStateStack.push(canvasState);

		// Remove values of the debugger watch
		let turtle = canvasState[0];
		this.watchService.updateDebugWatch(turtle);

		// Enable stepping buttons
        this.et.enableButton(this.et.getNextButton());
        if (this.getStateStackLength() > 1) {
            this.et.enableButton(this.et.getPrevButton());
		}
		
		// Update markers for commands
		this.updateMarkers(ctx);

		// Set cursor position to move the editors
		this.adjustCursorPosition(ctx)

		return DebuggerService.EXECUTION_SUSPEND;
    }

	// Clears memory and removes markers
    public endSession(): void {
		this.visitorStateStack = new Array();
		this.canvasStateStack = new Array();
		
        this.sessionMarkerNext.removeMarker(this.markerNext);
        this.sessionMarkerPrev.removeMarker(this.markerPrev);
        this.markerStack = new Array();
    }

	// Returns how many states we currently saved
    public getStateStackLength(): number {
        return this.visitorStateStack.length;
	}

	// Adjusts the cursor position in the editors to make the next command visible
	private adjustCursorPosition(ctx) {
		const row = ctx.start.line - 1;
		const colStart = ctx.start.column;
		const colEnd = ctx.stop.column + ctx.stop.stop - ctx.stop.start;

		// Check which editor the command comes from and overshoot cursor position a little
		if (row == 0) {
			const editor = this.commandsService.inputLine;
			editor.selection.moveTo(row, colEnd + 5);
			editor.selection.moveTo(row, colStart - 5);
		} else {
			const editor = AppComponent.editor;
			editor.selection.moveTo(row + 2, colEnd);
			editor.selection.moveTo(row - 2, colStart);
		}
	}

	// Updates the editor markers for next and previous commands
	private updateMarkers(ctx): void {
        // Remove old markers
        this.sessionMarkerNext.removeMarker(this.markerNext);
        this.sessionMarkerPrev.removeMarker(this.markerPrev);

        // Add new marker for next command
        let nextRow = ctx.start.line - 1;
        let nextColStart = ctx.start.column;
        let nextColEnd = ctx.stop.column + ctx.stop.stop - ctx.stop.start;
        this.markerNext = this.highlightRange(nextRow, nextColStart, nextColEnd, "markerNextCmd");

        // Add new marker for previous command
        if (this.markerStack.length > 0) {
            let oldMarkerCoords = this.markerStack.slice(-1)[0];
            let prevRow = oldMarkerCoords[0];
            let prevColStart = oldMarkerCoords[1];
            let prevColEnd = oldMarkerCoords[2];
            this.markerPrev = this.highlightRange(prevRow, prevColStart, prevColEnd, "markerPrevCmd");
        }

        // Update marker stack
        this.markerStack.push([nextRow, nextColStart, nextColEnd]);
	}
	
	// Returns the current depth of the callstack for a given context
    private getCallstackDepth(ctx): number {
		if (ctx === null) {
			return 0;
		}

		let name = DebuggerService.getOperationName(ctx);
		if (name == 'visitProcedureDeclaration') {
			return 1 + this.getCallstackDepth(ctx.parentCtx);
		} else {
			return this.getCallstackDepth(ctx.parentCtx);
		}
    }
	
	// Extracts a readable string from the constructor of the context
    static getOperationName(ctx): string {
        var operationConstructorString = ctx.constructor.toString().substr('function '.length);
        operationConstructorString = operationConstructorString.substr(0, operationConstructorString.indexOf('(') - 7);
        return "visit" + operationConstructorString;
	}
	
	// Highlights a certain range in the editor (or inputline) by adding a marker to it
	private highlightRange(row, colStart, colEnd, color): void {
		let Range = ace.require("ace/range").Range
		let range = new Range(row, colStart, row, colEnd + 1);

		let session;
		if (row == 0) {
			session = this.commandsService.inputLine.session;
		} else {
			session = AppComponent.editor.session;
		}
		let marker = session.addMarker(range, color, "text");

		// Save which session this marker belongs to
		if (color == "markerNextCmd") {
			this.sessionMarkerNext = session;
		} else {
			this.sessionMarkerPrev = session;
		}

		return marker;
	}

	// This bad boi clones so well I call him John Hammond
	private deepClone(ctx, hashmap = new WeakMap()): any {
		// Functions, primitives and null
		if (Object(ctx) !== ctx || ctx instanceof Function || !ctx) {
			return ctx;
		}
	
		// Do not further copy cyclic references
		if (hashmap.has(ctx)) {
			return hashmap.get(ctx);
		}
	
		// Create copy from constructor
		try {
			var cpy = new ctx.constructor();
		} catch (e) {
			var cpy = Object.create(Object.getPrototypeOf(ctx));
		}
		
		hashmap.set(ctx, cpy);
	
		// Recursively copy values
		for (var key in ctx) {
			if (ctx.hasOwnProperty(key)) {
				cpy[key] = this.deepClone(ctx[key], hashmap);
			}
		}
	
		return cpy;
	}

	//##################################################################################
	//TODO: These are dummy implementation to make compiler run through. Proper integration work needed.
	//##################################################################################

	public setEditor(editor: any) {
		//this.editor = editor;
	}

	public setInitialSessionMarkers(session: any) {
		this.sessionMarkerNext = session;
		this.sessionMarkerPrev = session;
	}
}


// WEBPACK FOOTER //
// ./src/app/services/debugger.service.ts